/**
 * Canonical analytics taxonomy for Discover.
 *
 * Every event name follows the shape: `Discover | <Area> | <Action>`.
 * Call sites should reference the `EVENTS` constants (or `buildEventName` /
 * `useTrackEvent`) rather than hand-writing strings, so the taxonomy stays
 * consistent across the app and groups cleanly in Mixpanel.
 *
 * See `docs/analytics-tracking-plan.md` for the full catalog and rationale.
 */
export const ANALYTICS_PRODUCT = "Discover";
export var AnalyticsArea;
(function (AnalyticsArea) {
    AnalyticsArea["Auth"] = "Auth";
    AnalyticsArea["Landing"] = "Landing";
    AnalyticsArea["Demo"] = "Demo";
    AnalyticsArea["Onboarding"] = "Onboarding";
    AnalyticsArea["Home"] = "Home";
    AnalyticsArea["Navigation"] = "Navigation";
    AnalyticsArea["NewReport"] = "New Report";
    AnalyticsArea["Report"] = "Report";
    AnalyticsArea["Results"] = "Results";
    AnalyticsArea["Overview"] = "Overview";
    AnalyticsArea["Community"] = "Community";
    AnalyticsArea["Portfolio"] = "Portfolio";
    AnalyticsArea["Recommendations"] = "Recommendations";
    AnalyticsArea["Subtopics"] = "Subtopics";
    AnalyticsArea["ReportSettings"] = "Report Settings";
    AnalyticsArea["Billing"] = "Billing";
    AnalyticsArea["Checkout"] = "Checkout";
    AnalyticsArea["Export"] = "Export";
    AnalyticsArea["UserSettings"] = "User Settings";
    AnalyticsArea["Admin"] = "Admin";
    AnalyticsArea["MobileGate"] = "Mobile Gate";
    AnalyticsArea["Sidebar"] = "Sidebar";
    AnalyticsArea["WhatsNew"] = "What's New";
    AnalyticsArea["Error"] = "Error";
})(AnalyticsArea || (AnalyticsArea = {}));
/** Builds a taxonomy-compliant event name: `Discover | <Area> | <Action>`. */
export const buildEventName = (area, action) => `${ANALYTICS_PRODUCT} | ${area} | ${action}`;
/**
 * Canonical event-name constants for the net-new events introduced by
 * DISCOVER-739 (items 2-17 of the tracking plan). Page views (item 1) are
 * handled centrally by `usePageTracking` and have no constant here.
 */
export const EVENTS = {
    whatsNew: {
        drawerOpened: buildEventName(AnalyticsArea.WhatsNew, "Drawer Opened"),
        drawerEntryClicked: buildEventName(AnalyticsArea.WhatsNew, "Drawer Entry Clicked"),
        drawerLoadMore: buildEventName(AnalyticsArea.WhatsNew, "Drawer Load More"),
        demoStarted: buildEventName(AnalyticsArea.WhatsNew, "Demo Started"),
        demoCompleted: buildEventName(AnalyticsArea.WhatsNew, "Demo Completed"),
        popupOpened: buildEventName(AnalyticsArea.WhatsNew, "Pop-up Opened"),
        slideSeen: buildEventName(AnalyticsArea.WhatsNew, "Slide Seen"),
        mainButtonClicked: buildEventName(AnalyticsArea.WhatsNew, "Main Button Clicked"),
        popupClosed: buildEventName(AnalyticsArea.WhatsNew, "Pop-up Closed"),
    },
    auth: {
        loginSucceeded: buildEventName(AnalyticsArea.Auth, "Login Succeeded"),
        loginFailed: buildEventName(AnalyticsArea.Auth, "Login Failed"),
        loggedOut: buildEventName(AnalyticsArea.Auth, "Logged Out"),
        invalidTokenHit: buildEventName(AnalyticsArea.Auth, "Invalid Token Hit"),
        setPasswordStarted: buildEventName(AnalyticsArea.Auth, "Set Password Started"),
        setPasswordSubmitted: buildEventName(AnalyticsArea.Auth, "Set Password Submitted"),
        setPasswordDismissed: buildEventName(AnalyticsArea.Auth, "Set Password Dismissed"),
    },
    landing: {
        signupClicked: buildEventName(AnalyticsArea.Landing, "Signup Clicked"),
        signupSucceeded: buildEventName(AnalyticsArea.Landing, "Signup Succeeded"),
        signupFailed: buildEventName(AnalyticsArea.Landing, "Signup Failed"),
    },
    newReport: {
        started: buildEventName(AnalyticsArea.NewReport, "Started"),
        communitySearched: buildEventName(AnalyticsArea.NewReport, "Community Searched"),
        communitySelected: buildEventName(AnalyticsArea.NewReport, "Community Selected"),
        submitted: buildEventName(AnalyticsArea.NewReport, "Submitted"),
    },
    report: {
        generationStarted: buildEventName(AnalyticsArea.Report, "Generation Started"),
        generationCompleted: buildEventName(AnalyticsArea.Report, "Generation Completed"),
        generationFailed: buildEventName(AnalyticsArea.Report, "Generation Failed"),
    },
    community: {
        viewed: buildEventName(AnalyticsArea.Community, "Viewed"),
        restrictedHit: buildEventName(AnalyticsArea.Community, "Restricted Hit"),
    },
    portfolio: {
        viewed: buildEventName(AnalyticsArea.Portfolio, "Viewed"),
        trendModelFilterChanged: buildEventName(AnalyticsArea.Portfolio, "Trend Model Filter Changed"),
        metricDefinitionViewed: buildEventName(AnalyticsArea.Portfolio, "Metric Definition Viewed"),
        countClicked: buildEventName(AnalyticsArea.Portfolio, "Count Clicked"),
    },
    overview: {
        viewed: buildEventName(AnalyticsArea.Overview, "Viewed"),
    },
    navigation: {
        communitySearched: buildEventName(AnalyticsArea.Navigation, "Community Searched"),
        communityExpanded: buildEventName(AnalyticsArea.Navigation, "Community Expanded"),
        communitySwitched: buildEventName(AnalyticsArea.Navigation, "Community Switched"),
        reportPageOpened: buildEventName(AnalyticsArea.Navigation, "Report Page Opened"),
        reportDateSelected: buildEventName(AnalyticsArea.Navigation, "Report Date Selected"),
        sectionJumped: buildEventName(AnalyticsArea.Navigation, "Section Jumped"),
        portfolioOverviewClicked: buildEventName(AnalyticsArea.Navigation, "Portfolio Overview Clicked"),
        legacyLinkRedirected: buildEventName(AnalyticsArea.Navigation, "Legacy Link Redirected"),
    },
    reportSettings: {
        trackedCompetitorsViewed: buildEventName(AnalyticsArea.ReportSettings, "Tracked Competitors Viewed"),
        competitorSearched: buildEventName(AnalyticsArea.ReportSettings, "Competitor Searched"),
        competitorAdded: buildEventName(AnalyticsArea.ReportSettings, "Competitor Added"),
        competitorAddRejected: buildEventName(AnalyticsArea.ReportSettings, "Competitor Add Rejected"),
        competitorRemoved: buildEventName(AnalyticsArea.ReportSettings, "Competitor Removed"),
        competitorsCleared: buildEventName(AnalyticsArea.ReportSettings, "Competitors Cleared"),
        manualModeOpened: buildEventName(AnalyticsArea.ReportSettings, "Manual Mode Opened"),
        trackedCompetitorsSaved: buildEventName(AnalyticsArea.ReportSettings, "Tracked Competitors Saved"),
        trackedCompetitorsSaveFailed: buildEventName(AnalyticsArea.ReportSettings, "Tracked Competitors Save Failed"),
    },
    userSettings: {
        clicked: buildEventName(AnalyticsArea.UserSettings, "Clicked"),
        usersSearched: buildEventName(AnalyticsArea.UserSettings, "Users Searched"),
        usersFiltered: buildEventName(AnalyticsArea.UserSettings, "Users Filtered"),
        addUserClicked: buildEventName(AnalyticsArea.UserSettings, "Add User Clicked"),
        userCreated: buildEventName(AnalyticsArea.UserSettings, "User Created"),
        userCreateFailed: buildEventName(AnalyticsArea.UserSettings, "User Create Failed"),
        userEditClicked: buildEventName(AnalyticsArea.UserSettings, "User Edit Clicked"),
        userUpdated: buildEventName(AnalyticsArea.UserSettings, "User Updated"),
        userUpdateFailed: buildEventName(AnalyticsArea.UserSettings, "User Update Failed"),
        userStatusToggled: buildEventName(AnalyticsArea.UserSettings, "User Status Toggled"),
        inviteResent: buildEventName(AnalyticsArea.UserSettings, "Invite Resent"),
        userDeleted: buildEventName(AnalyticsArea.UserSettings, "User Deleted"),
    },
    admin: {
        dashboardViewed: buildEventName(AnalyticsArea.Admin, "Dashboard Viewed"),
    },
    mobileGate: {
        shown: buildEventName(AnalyticsArea.MobileGate, "Shown"),
        continueAnyway: buildEventName(AnalyticsArea.MobileGate, "Continue Anyway"),
    },
    error: {
        apiRequestFailed: buildEventName(AnalyticsArea.Error, "API Request Failed"),
        pageNotFound: buildEventName(AnalyticsArea.Error, "Page Not Found"),
    },
};
/**
 * GA4 recommended conversion events. Unlike the `Discover | <Area> | <Action>` taxonomy above, these
 * names are fixed by Google: GA4 only accepts event names made of letters, digits and underscores,
 * and the Google Ads / GA4 key-event tooling recognises these exact names. The GA plugin lower-cases
 * and underscores whatever it is given, so the taxonomy names above reach GA4 as e.g.
 * `discover_|_demo_|_...`, which is not a valid GA4 event name.
 *
 * Send them with `GA4_ONLY_OPTIONS` so they reach Google Analytics alone: the Mixpanel / Peek
 * analytics side already has its own taxonomy event for the same moment, and must not grow a
 * second, differently named one.
 */
export const GA4_EVENTS = {
    /** A prospect's demo request was accepted (the `/demo` form, or its free-report auto-submit). */
    generateLead: "generate_lead",
    /** A self-serve account was created from the landing / embedded signup form. */
    signUp: "sign_up",
};
/** `analytics.track` options that deliver an event to the Google Analytics plugin only. */
export const GA4_ONLY_OPTIONS = { plugins: { all: false, "google-analytics": true } };
